import { FormEvent, useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { BookingBrandHeader } from "@/components/BookingBrandHeader";
import { BookingTermsAcceptance } from "@/components/BookingTermsAcceptance";
import { PrimaryGuestFieldsForm } from "@/components/PrimaryGuestFieldsForm";
import { useLocale } from "@/i18n";
import { api, ApiError } from "@/lib/api";
import { bookingLegalContextFromConfig } from "@/lib/bookingLegalTexts";
import {
  DEFAULT_PRIMARY_GUEST_FIELDS,
  buildPrimaryGuestPayload,
  emptyPrimaryGuestForm,
  validatePrimaryGuest,
  type PrimaryGuestFormData,
  type PrimaryGuestFieldsConfig,
} from "@/lib/primaryGuestFields";
import {
  EMPTY_VEHICLE_FORM,
  VEHICLE_FIELD_LABELS,
  vehicleFieldUnit,
  activeVehicleFields,
  vehicleFieldsConfigFor,
  type VehicleFormData,
} from "@/lib/vehicleFields";
import { formatCurrency } from "@/lib/money";
import { useCampsiteBranding } from "@/lib/useCampsiteBranding";
import type { PublicBookingConfig } from "@/types";

type KnownGuest = {
  title: string | null;
  first_name: string;
  middle_name: string | null;
  last_name: string;
  company_name: string | null;
  vat_id: string | null;
  email: string;
  phone: string | null;
  date_of_birth: string | null;
  nationality: string | null;
  street: string | null;
  house_number: string | null;
  address_line_2: string | null;
  postal_code: string | null;
  city: string | null;
  state_region: string | null;
  country_code: string | null;
  license_plate: string | null;
  vehicle_length_m: string | number | null;
  vehicle_width_m: string | number | null;
  vehicle_height_m: string | number | null;
  vehicle_weight_kg: string | number | null;
  tent_size: string | null;
};

type CompletionData = {
  booking_number: string;
  arrival_date: string;
  departure_date: string;
  hold_until: string;
  expires_in_seconds: number;
  first_name: string;
  last_name: string;
  email: string;
  arrival_type: string;
  pitch_category_id: string | null;
  pitch_category_name: string | null;
  adults: number;
  children: number;
  dogs: number;
  wants_electricity: boolean;
  notes: string | null;
  completed: boolean;
  pitch_categories: { id: string; name: string }[];
  config: PublicBookingConfig;
  known_guest?: KnownGuest | null;
  deposit_label?: string | null;
  deposit_amount?: string | number | null;
  deposit_currency?: string;
  online_payment?: boolean;
  payment_open?: boolean;
  payment_status?: string | null;
};

type CompletionResult = {
  booking: { booking_number: string };
  message: string;
  checkout_url?: string | null;
  deposit_amount?: string | number | null;
  deposit_label?: string | null;
  payment_required?: boolean;
};

function formatDate(iso: string, locale: string): string {
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return iso;
  if (locale === "en") return `${d}/${m}/${y}`;
  return `${d}.${m}.${y}`;
}

function formatDateTime(iso: string, locale: string): string {
  try {
    return new Intl.DateTimeFormat(locale === "en" ? "en-GB" : "de-DE", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function textOrEmpty(value: string | null | undefined): string {
  const text = (value || "").trim();
  return text === "–" ? "" : text;
}

function applyKnownGuest(
  form: PrimaryGuestFormData,
  known: KnownGuest | null | undefined,
): PrimaryGuestFormData {
  if (!known) return form;
  const next = { ...form };
  const assign = (key: keyof PrimaryGuestFormData, value: string | null | undefined) => {
    const text = textOrEmpty(value);
    if (text) next[key] = text;
  };
  assign("title", known.title);
  assign("first_name", known.first_name);
  assign("middle_name", known.middle_name);
  assign("last_name", known.last_name);
  assign("company_name", known.company_name);
  assign("vat_id", known.vat_id);
  assign("email", known.email);
  assign("phone", known.phone);
  assign("date_of_birth", known.date_of_birth);
  assign("nationality", known.nationality);
  assign("street", known.street);
  assign("house_number", known.house_number);
  assign("address_line_2", known.address_line_2);
  assign("postal_code", known.postal_code);
  assign("city", known.city);
  assign("state_region", known.state_region);
  assign("country_code", known.country_code);
  return next;
}

export function PhoneBookingCompletePage() {
  const { token = "" } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const paidReturn = searchParams.get("paid") === "1";
  const cancelledReturn = searchParams.get("cancelled") === "1";
  const { locale, localePath, messages } = useLocale();
  const copy = messages.phoneComplete;
  const { siteName, logoUrl } = useCampsiteBranding();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(cancelledReturn ? copy.paymentCancelled : null);
  const [data, setData] = useState<CompletionData | null>(null);
  const [done, setDone] = useState<"confirmed" | "already" | null>(null);
  const [doneBookingNumber, setDoneBookingNumber] = useState("");
  const [primaryGuestForm, setPrimaryGuestForm] = useState<PrimaryGuestFormData>(
    emptyPrimaryGuestForm(),
  );
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  const [dogs, setDogs] = useState(0);
  const [wantsElectricity, setWantsElectricity] = useState(true);
  const [vehicleForm, setVehicleForm] = useState<VehicleFormData>({ ...EMPTY_VEHICLE_FORM });
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [notes, setNotes] = useState("");
  const [selectedFeatureIds, setSelectedFeatureIds] = useState<string[]>([]);
  const [arrivalType, setArrivalType] = useState("");
  const [categoryId, setCategoryId] = useState("");

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    let attempts = 0;

    async function load() {
      try {
        const res = await api.get<CompletionData>(`/api/v1/public/booking/complete/${token}`);
        if (cancelled) return;
        setData(res);
        setPrimaryGuestForm(applyKnownGuest(emptyPrimaryGuestForm(), res.known_guest));
        setArrivalType(res.arrival_type || "");
        setCategoryId(res.pitch_category_id || "");
        setAdults(res.adults || 2);
        setChildren(res.children || 0);
        setDogs(res.dogs || 0);
        setWantsElectricity(res.wants_electricity);
        const vehicle = { ...EMPTY_VEHICLE_FORM };
        const known = res.known_guest;
        if (known?.license_plate) vehicle.license_plate = known.license_plate;
        if (known?.vehicle_length_m != null) vehicle.vehicle_length_m = String(known.vehicle_length_m);
        if (known?.vehicle_width_m != null) vehicle.vehicle_width_m = String(known.vehicle_width_m);
        if (known?.vehicle_height_m != null) vehicle.vehicle_height_m = String(known.vehicle_height_m);
        if (known?.vehicle_weight_kg != null) vehicle.vehicle_weight_kg = String(known.vehicle_weight_kg);
        if (known?.tent_size) vehicle.tent_size = known.tent_size;
        setVehicleForm(vehicle);
        if (res.payment_status === "failed") {
          setError(copy.paymentFailed);
          setLoading(false);
          return;
        }
        if (res.payment_status === "expired") {
          setNotice(copy.paymentExpired);
          setLoading(false);
          return;
        }
        if (res.completed && !res.payment_open) {
          setDoneBookingNumber(res.booking_number);
          setDone(paidReturn ? "confirmed" : "already");
          setLoading(false);
          return;
        }
        if (res.payment_status === "processing" && paidReturn) {
          setNotice(copy.paymentProcessing);
        }
        if (res.completed && res.payment_open && paidReturn && attempts < 8) {
          attempts += 1;
          setLoading(false);
          window.setTimeout(() => {
            if (!cancelled) void load();
          }, 2000);
          return;
        }
        if (paidReturn && res.payment_open && attempts >= 8) {
          if (res.payment_status === "processing") setNotice(copy.paymentProcessing);
          else setError(copy.paymentPending);
        }
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        setLoading(false);
        setError(err instanceof ApiError ? err.message : copy.invalidLink);
      }
    }

    setLoading(true);
    setError(null);
    void load();
    return () => {
      cancelled = true;
    };
  }, [
    token,
    paidReturn,
    copy.invalidLink,
    copy.paymentPending,
    copy.paymentFailed,
    copy.paymentExpired,
    copy.paymentProcessing,
  ]);

  const primaryConfig: PrimaryGuestFieldsConfig = useMemo(
    () => data?.config.primary_guest_fields ?? DEFAULT_PRIMARY_GUEST_FIELDS,
    [data],
  );

  const vehicleFieldConfig = useMemo(() => {
    if (!data) return vehicleFieldsConfigFor("", []);
    return vehicleFieldsConfigFor(arrivalType || data.arrival_type, data.config.arrival_types);
  }, [arrivalType, data]);

  const vehicleKeys = activeVehicleFields(vehicleFieldConfig);
  const showElectricity = data?.config.flow?.show_electricity_option !== false;
  const showDogs = data?.config.flow?.show_dogs_field !== false;
  const showChildren = data?.config.flow?.show_children_field !== false;
  const legalContext = useMemo(
    () =>
      bookingLegalContextFromConfig(
        data?.config.online_booking_legal,
        data?.config.site_name?.trim() || siteName,
      ),
    [data, siteName],
  );

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!data || done) return;
    setError(null);
    const guestError = validatePrimaryGuest(primaryGuestForm, primaryConfig);
    if (guestError) {
      setError(guestError);
      return;
    }
    if (!primaryGuestForm.first_name.trim() || primaryGuestForm.first_name.trim() === "–") {
      setError(copy.firstNameRequired);
      return;
    }
    if (!primaryGuestForm.last_name.trim() || primaryGuestForm.last_name.trim() === "–") {
      setError(copy.lastNameRequired);
      return;
    }
    if (!data.arrival_type && !arrivalType) {
      setError(copy.arrivalTypeRequired);
      return;
    }
    if (!data.pitch_category_id && !categoryId) {
      setError(copy.categoryRequired);
      return;
    }
    for (const key of vehicleKeys) {
      if (vehicleFieldConfig[key] === "required" && !vehicleForm[key]?.trim()) {
        setError(copy.vehicleRequired);
        return;
      }
    }
    if (!acceptTerms) {
      setError(messages.flow.acceptTerms);
      return;
    }

    setSubmitting(true);
    try {
      const primaryPayload = buildPrimaryGuestPayload(primaryGuestForm, primaryConfig);
      const body: Record<string, unknown> = {
        ...primaryPayload,
        adults,
        children: showChildren ? children : 0,
        dogs: showDogs ? dogs : 0,
        wants_electricity: showElectricity ? wantsElectricity : true,
        accept_terms: true,
        notes: notes.trim() || null,
        requested_feature_ids: selectedFeatureIds,
        arrival_type: data.arrival_type || arrivalType || null,
        pitch_category_id: data.pitch_category_id || categoryId || null,
      };
      for (const key of vehicleKeys) {
        const raw = vehicleForm[key]?.trim();
        if (!raw) continue;
        if (
          key === "vehicle_length_m" ||
          key === "vehicle_width_m" ||
          key === "vehicle_height_m" ||
          key === "vehicle_weight_kg"
        ) {
          body[key] = Number(raw.replace(",", "."));
        } else {
          body[key] = raw;
        }
      }
      const result = await api.post<CompletionResult>(
        `/api/v1/public/booking/complete/${token}`,
        body,
      );
      if (result.checkout_url) {
        window.location.assign(result.checkout_url);
        return;
      }
      if (result.payment_required) {
        setError(copy.saveError);
        setData((current: CompletionData | null) =>
          current
            ? {
                ...current,
                completed: true,
                payment_open: true,
                deposit_amount: result.deposit_amount ?? current.deposit_amount,
                deposit_label: result.deposit_label ?? current.deposit_label,
              }
            : current,
        );
        return;
      }
      setDoneBookingNumber(result.booking.booking_number);
      setDone("confirmed");
      if (result.deposit_amount != null && !result.payment_required) {
        setNotice(copy.confirmedDepositDue(formatCurrency(result.deposit_amount, "EUR", locale)));
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : copy.saveError);
    } finally {
      setSubmitting(false);
    }
  }

  async function payDeposit() {
    if (!token) return;
    setPaying(true);
    setError(null);
    try {
      const result = await api.post<CompletionResult>(
        `/api/v1/public/booking/complete/${token}/deposit`,
        {},
      );
      if (result.checkout_url) {
        window.location.assign(result.checkout_url);
        return;
      }
      setNotice(result.message);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : copy.saveError);
    } finally {
      setPaying(false);
    }
  }

  const depositAmountLabel =
    data?.deposit_amount != null
      ? formatCurrency(data.deposit_amount, data.deposit_currency || "EUR", locale)
      : null;
  const showPaymentPanel = Boolean(data?.completed && data.payment_open && !done);

  return (
    <div className="mx-auto min-h-screen max-w-lg px-4 py-10">
      <BookingBrandHeader
        siteName={siteName}
        logoUrl={logoUrl}
        title={copy.pageTitle}
        subtitle={copy.pageSubtitle}
        className="mb-8"
      />

      <div className="bk-card space-y-4">
        {loading ? (
          <p className="text-sm text-slate-600">{copy.loading}</p>
        ) : error && !data ? (
          <div className="space-y-3">
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert">
              {error}
            </div>
            <p className="text-center text-sm text-slate-500">
              <Link to={localePath("/")} className="text-brand-700 underline">
                {copy.toBooking}
              </Link>
            </p>
          </div>
        ) : done ? (
          <div className="space-y-3">
            <div className="rounded-lg border border-brand-200 bg-brand-50 px-4 py-3 text-sm text-brand-900">
              {notice
                ? notice
                : done === "already"
                  ? copy.alreadyDone
                  : paidReturn
                    ? copy.confirmedPaid
                    : copy.confirmed}
            </div>
            <p className="text-sm text-slate-600">
              {copy.bookingNumber}: <strong>{doneBookingNumber}</strong>
            </p>
          </div>
        ) : showPaymentPanel && data ? (
          <div className="space-y-4">
            {notice ? (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                {notice}
              </div>
            ) : null}
            <p className="text-sm text-slate-700">
              {copy.bookingNumber}: <strong>{data.booking_number}</strong>
            </p>
            <p className="text-sm text-slate-700">
              {copy.depositTitle}
              {depositAmountLabel ? `: ${depositAmountLabel}` : ""}
              {data.deposit_label ? ` (${data.deposit_label})` : ""}
            </p>
            {error ? (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert">
                {error}
              </div>
            ) : null}
            <button
              type="button"
              className="bk-btn-primary w-full"
              disabled={paying || data.payment_status === "processing"}
              onClick={() => void payDeposit()}
            >
              {paying ? copy.paying : copy.payDeposit}
            </button>
          </div>
        ) : data ? (
          <form className="space-y-5" onSubmit={(e) => void onSubmit(e)}>
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 space-y-1">
              <p>
                <span className="text-slate-500">{copy.booking}:</span> {data.booking_number}
              </p>
              <p>
                <span className="text-slate-500">{copy.period}:</span>{" "}
                {formatDate(data.arrival_date, locale)} – {formatDate(data.departure_date, locale)}
              </p>
              {data.pitch_category_name ? (
                <p>
                  <span className="text-slate-500">{copy.category}:</span> {data.pitch_category_name}
                </p>
              ) : null}
              <p className="font-medium text-amber-800">
                {copy.deadline(formatDateTime(data.hold_until, locale))}
              </p>
              {data.deposit_label ? (
                <p>
                  <span className="text-slate-500">{copy.depositTitle}:</span>{" "}
                  {depositAmountLabel
                    ? copy.depositEstimate(data.deposit_label, depositAmountLabel)
                    : data.deposit_label}
                </p>
              ) : null}
              {data.online_payment ? (
                <p>{copy.depositOnline}</p>
              ) : depositAmountLabel ? (
                <p>{copy.depositOffline(depositAmountLabel)}</p>
              ) : null}
            </div>

            {notice ? (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                {notice}
              </div>
            ) : null}

            {!data.arrival_type ? (
              <fieldset className="space-y-2">
                <legend className="text-sm font-medium text-slate-800">{copy.arrivalType}</legend>
                <div className="flex flex-wrap gap-2">
                  {(data.config.arrival_types ?? []).map((type) => {
                    const selected = arrivalType === type.value;
                    return (
                      <button
                        key={type.value}
                        type="button"
                        onClick={() => setArrivalType(type.value)}
                        className={
                          selected
                            ? "rounded-full bg-brand-700 px-3 py-1.5 text-sm font-medium text-white"
                            : "rounded-full border border-slate-300 px-3 py-1.5 text-sm text-slate-700"
                        }
                      >
                        {type.label}
                      </button>
                    );
                  })}
                </div>
              </fieldset>
            ) : null}

            {!data.pitch_category_id ? (
              <fieldset className="space-y-2">
                <legend className="text-sm font-medium text-slate-800">{copy.category}</legend>
                <div className="grid gap-2">
                  {(data.pitch_categories ?? []).map((category) => {
                    const selected = categoryId === category.id;
                    return (
                      <button
                        key={category.id}
                        type="button"
                        onClick={() => setCategoryId(category.id)}
                        className={
                          selected
                            ? "rounded-lg border border-brand-700 bg-brand-50 px-3 py-2 text-left text-sm font-medium text-brand-900"
                            : "rounded-lg border border-slate-300 px-3 py-2 text-left text-sm text-slate-700"
                        }
                      >
                        {category.name}
                      </button>
                    );
                  })}
                </div>
              </fieldset>
            ) : null}

            <PrimaryGuestFieldsForm
              form={primaryGuestForm}
              config={primaryConfig}
              onChange={setPrimaryGuestForm}
              heading={copy.yourData}
              googlePlacesApiKey={data.config.google_places_api_key}
            />

            <div className="grid gap-3 sm:grid-cols-3">
              <label className="block text-sm font-medium text-slate-700">
                {messages.common.adults}
                <input
                  type="number"
                  min={1}
                  max={20}
                  className="bk-input mt-1 w-full"
                  value={adults}
                  onChange={(e) => setAdults(Number(e.target.value) || 1)}
                />
              </label>
              {showChildren ? (
                <label className="block text-sm font-medium text-slate-700">
                  {messages.common.children}
                  <input
                    type="number"
                    min={0}
                    max={20}
                    className="bk-input mt-1 w-full"
                    value={children}
                    onChange={(e) => setChildren(Number(e.target.value) || 0)}
                  />
                </label>
              ) : null}
              {showDogs ? (
                <label className="block text-sm font-medium text-slate-700">
                  {messages.common.dogs}
                  <input
                    type="number"
                    min={0}
                    max={10}
                    className="bk-input mt-1 w-full"
                    value={dogs}
                    onChange={(e) => setDogs(Number(e.target.value) || 0)}
                  />
                </label>
              ) : null}
            </div>

            {showElectricity ? (
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={wantsElectricity}
                  onChange={(e) => setWantsElectricity(e.target.checked)}
                />
                {copy.electricity}
              </label>
            ) : null}

            {(data.config.pitch_features?.length ?? 0) > 0 ? (
              <fieldset className="space-y-2 rounded-lg border border-slate-200 p-3">
                <legend className="px-1 text-sm font-medium text-slate-800">{copy.wishes}</legend>
                <p className="text-xs text-slate-500">{copy.wishesHint}</p>
                {data.config.pitch_features?.map((feature) => (
                  <label key={feature.id} className="flex cursor-pointer items-start gap-2 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4 rounded border-slate-300"
                      checked={selectedFeatureIds.includes(feature.id)}
                      onChange={(e) => {
                        setSelectedFeatureIds((prev) =>
                          e.target.checked
                            ? [...prev, feature.id]
                            : prev.filter((id) => id !== feature.id),
                        );
                      }}
                    />
                    <span>
                      <span className="font-medium text-slate-900">{feature.name}</span>
                      {feature.description ? (
                        <span className="mt-0.5 block text-xs text-slate-500">{feature.description}</span>
                      ) : null}
                    </span>
                  </label>
                ))}
              </fieldset>
            ) : null}

            {vehicleKeys.length > 0 ? (
              <div className="space-y-3">
                <h2 className="text-sm font-semibold text-slate-800">{copy.vehicle}</h2>
                {vehicleKeys.map((key) => (
                  <label key={key} className="block text-sm font-medium text-slate-700">
                    {VEHICLE_FIELD_LABELS[key]}
                    {vehicleFieldUnit(key) ? ` (${vehicleFieldUnit(key)})` : ""}
                    {vehicleFieldConfig[key] === "required" ? " *" : ""}
                    <input
                      className="bk-input mt-1 w-full"
                      value={vehicleForm[key]}
                      onChange={(e) =>
                        setVehicleForm((prev) => ({ ...prev, [key]: e.target.value }))
                      }
                      required={vehicleFieldConfig[key] === "required"}
                    />
                  </label>
                ))}
              </div>
            ) : null}

            <label className="block text-sm font-medium text-slate-700">
              {copy.notes}
              <textarea
                className="bk-input mt-1 w-full"
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </label>

            <BookingTermsAcceptance
              checked={acceptTerms}
              onChange={setAcceptTerms}
              locale={locale}
              legalContext={legalContext}
              termsPrefix={copy.termsPrefix}
              termsAnd={messages.flow.termsAnd}
              termsSuffix={copy.termsSuffix}
              termsLink={messages.flow.termsLink}
              privacyLink={messages.flow.privacyLink}
              closeLabel={messages.flow.legalClose}
            />

            {error ? (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert">
                {error}
              </div>
            ) : null}

            <button type="submit" className="bk-btn-primary w-full" disabled={submitting || !acceptTerms}>
              {submitting ? copy.saving : data.online_payment ? copy.submitAndPay : copy.submit}
            </button>
          </form>
        ) : null}
      </div>
    </div>
  );
}
